// ================= THE PASSIVE DICTIONARY =================
//
// ONE SOURCE FOR WHAT EACH FOUNDATION DOES FOR FREE, everywhere a player can read it: the Zone 1
// HUD, the resolution beats, and the sheet either of them opens. Same job and same shape as
// js/mechanics-dictionary.js -- see that file's header for why the copy has to live in exactly one
// place -- and deliberately a sibling of it rather than a section inside it, because the two answer
// different questions. The mechanics dictionary answers "what does ABSORB mean", which is a
// property of a card. This answers "what does my fighter do that no card of mine says", which is a
// property of the fighter and true for the whole bout.
//
// KEYED BY PASSIVE ID, NOT BY STYLE, and that is the whole shape of the change this file is part
// of. A passive belongs to the DECK now: js/engine.js resolves `fighter.passiveId` through its own
// PASSIVES registry, and at launch that id is derived from the deck's style via its Foundation. So
// this table is keyed the way the engine is keyed, and `forFighter()` does the style -> Foundation
// -> base resolution for callers that only hold a style. When a deck can carry its own equipped
// passive, every consumer of this file keeps working unchanged.
//
// THE EIGHT STYLE PASSIVES THIS REPLACES ARE RETIRED. They return later as style-locked
// equippables; their rules as last implemented are recorded in §8 of
// deploy_handoff/foundation-passives-plan.md, and the commit that retired them is where the code
// went. `TIMING.intent` below is kept for exactly that return -- four of the eight decided the
// Intent contest and none of the four bases does.
//
// EVERY ENTRY IS THE RULE AS js/engine.js RUNS IT, not as a design document describes it. The
// `trigger` strings are prose restatements of the actual branch conditions in autoPassiveClashHooks,
// autoPassiveEndHooks and the resolution-timing hook, and tests/style-passives-test.js walks this
// table against the events the engine actually emits -- name, id, timing, kind and the beat each
// one lands in -- so a rules change cannot leave the copy behind.
//
// TWO TABLES, ONE ASSERTION. The engine's PASSIVES carries id/foundation/name/timing/kind and this
// one carries the same plus the prose. Neither file may import the other (the engine cannot depend
// on a UI file, and this file must load on pages that have no engine), so the overlap is duplicated
// deliberately and the test asserts the two agree field by field -- the same pattern
// js/mechanics-dictionary.js uses for CONTROL_PHRASE against the engine's CONTROL_WORD.
//
// NO DEPENDENCIES BY DESIGN: it reads nothing but its own table and window.FAMILY_OF_STYLE /
// window.STYLE_LABELS from js/cards.js, so library.html and deck-builder.html can load it without
// the game shell.
//
// Loads after js/cards.js and before js/ui-shell.js, which renders the HUD line, and
// js/ui-resolution.js, which renders the beat callouts.
(function () {
  'use strict';

  function escapeHtml(str) {
    return String(str).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  // THE FOUR TIMINGS, in the words the rules use for them. A player who knows WHEN a passive is
  // checked can plan around it; one who only knows what it does cannot. Dictated Distance is the
  // clearest case -- you know your Intent and the range before you commit, so its condition is
  // yours to satisfy on purpose rather than something that happens to you.
  const TIMING = {
    // Unused at launch: none of the four bases contests Intent, which was a design decision made on
    // measured data (the four retired Intent passives fired 7-36 times across a full pairing sweep
    // against 87-202 for the track ones). Kept because the returning equippables use it.
    intent:     { key: 'intent',     label: 'Intent contest',  order: 1,
                  when: 'Checked when the two Intents are compared, before either Attack resolves.' },
    clash:      { key: 'clash',      label: 'Clash locked',    order: 2,
                  when: 'Checked once the Clash Range is locked, before the Attacks resolve.' },
    resolution: { key: 'resolution', label: 'On resolution',   order: 3,
                  when: 'Checked as the Attack resolves, alongside its printed clean-hit effects.' },
    end:        { key: 'end',        label: 'End of exchange', order: 4,
                  when: 'Checked at the end of the exchange, after both Attacks and the End Range.' }
  };

  // ---------------- The four ----------------
  //
  // `trigger` is the condition, `effect` is the consequence, and the pair is what the HUD tooltip
  // and the beat callout are built from. `long` is the sheet paragraph and is allowed to explain
  // WHY the rule is shaped the way it is; `sample` is a concrete exchange, because every one of
  // these is a conditional and a conditional without an instance is a definition, not an
  // explanation.
  //
  // `timing` AND `kind` ARE DIFFERENT QUESTIONS. `timing` is when the rule is checked; `kind` is
  // the shape of what it does, and is the `kind` field on the engine's style.passiveResolved event.
  // They agree for three of the four and diverge for Dictated Distance, which is checked at Clash
  // and pays out in Power.
  const ENTRIES = {
    dictated_distance: {
      key: 'dictated_distance', id: 'dictated_distance', foundation: 'KineticImpact',
      name: 'Dictated Distance', timing: 'clash', kind: 'power',
      trigger: 'Your Attack is Optimal at the Clash Range, and it wants that one range only.',
      effect: 'Your Attack gains +2 Power.',
      oneLiner: 'Commit to one range, get the fight there, and the strike lands twice as hard.',
      long: 'The striking arts win by making the fight happen at the range their technique was ' +
            'designed for, and this pays for the technique that has only one. A card listing two ' +
            'Optimal bands is hedging — it is comfortable in more of the fight, and it collects ' +
            'nothing here. The whole condition is yours to satisfy and neither half is free: you ' +
            'have to build with committed cards, and then win the Intent contest to put the Clash ' +
            'where one of them lives. Nothing about their card is read, so this is a plan you can ' +
            'make before you know what they played. Your own Improvised Strike switches it off; ' +
            'theirs is irrelevant.',
      sample: 'The Clash settles at Long. Your Driving Side Kick wants Long and nothing else, so ' +
              'its Power 3 resolves as Power 5. A roundhouse happy at Long OR Pocket would have ' +
              'been Optimal too, and would have collected nothing.'
    },
    compounding_pressure: {
      key: 'compounding_pressure', id: 'compounding_pressure', foundation: 'TorquePressure',
      name: 'Compounding Pressure', timing: 'end', kind: 'track',
      trigger: 'Your Attack dealt damage.',
      effect: 'Shift 1 toward Fury.',
      oneLiner: 'Anything you land builds Fury.',
      long: 'Offense feeding offense, with no qualifier on how it landed or what you threw — the ' +
            'broadest trigger of the four bases on purpose, because the Fury track has a ceiling ' +
            'and that ceiling is its governor. What it asks of you is simply that you keep getting ' +
            'through, which is what pressure means. An Improvised Strike does not count.',
      sample: 'Your hook gets through their guard for 2. At the end of the exchange you shift 1 ' +
              'toward Fury, and again next exchange if you land again.'
    },
    spent_force: {
      key: 'spent_force', id: 'spent_force', foundation: 'FulcrumFlow',
      name: 'Spent Force', timing: 'end', kind: 'restrict',
      trigger: 'Your Defense met their Attack with a Full Guard.',
      effect: 'They cannot choose that same Intent next exchange, and your next Defense gains +1 Guard.',
      oneLiner: 'Catch a real shot cleanly and they cannot come at you that way again — and you are set for the next one.',
      long: 'Their force, met at the pivot — and what you take is their approach rather than ' +
            'anything of theirs you could spend. A Full Guard is the strict version of stopping ' +
            'something: every level covered, nothing through, and the attack still had at least 1 ' +
            'Power when it arrived. Meet one and the Intent they came in on is closed to them next ' +
            'exchange, so Pressure that met a wall cannot simply press again. The +1 Guard is the ' +
            'same idea from your own side: you read the shot, so you are set for the next one. ' +
            'Both halves last exactly one exchange and the Guard is spent whether or not anyone ' +
            'throws into it — nothing here banks. Positioning is what this Foundation has always ' +
            'fought over, and this is the version of that you get for free.',
      sample: 'They Pressure in behind a hook; your Guard 4 catches its Power 3 cleanly. Next ' +
              'exchange they have to find you with Anchor or Evade, and whatever they find you ' +
              'with meets a Guard one higher than it is printed.'
    },
    uprooted: {
      key: 'uprooted', id: 'uprooted', foundation: 'LeverageBase',
      name: 'Uprooted', timing: 'resolution', kind: 'status',
      trigger: 'Your Grapple, Throw, Takedown or Sweep lands clean.',
      effect: 'They become Off-Balance — or, if they already were, you get an Opening at the level you landed on.',
      oneLiner: 'A clean throw takes their base; if it is already gone, you get the finish instead.',
      long: 'Break the structure first and finish second, in one rule. This is the only base ' +
            'passive that changes the opponent rather than your own numbers, and it feeds the whole ' +
            'Leverage & Base card pool rather than sitting beside it — a great many of those cards ' +
            'ask for an Off-Balance opponent, and this is where one comes from. Landing a second ' +
            'throw on someone already broken is not wasted: Off-Balance is a flag and cannot stack, ' +
            'so the second one pays an Opening instead. Distance does not gate it: a throw that ' +
            'got through cleanly has taken their base whether or not the range was awkward, and ' +
            'the Compromised rule in §5 is about printed card text rather than about what your ' +
            'fighter is.',
      sample: 'Your hip throw gets through clean and they are Off-Balance, which is exactly when ' +
              'your reaps become legal. Land the reap too and they are open where it hit.'
    }
  };

  // Foundation -> base passive id, derived from the table rather than written twice. Same trick the
  // engine uses on its own registry, and for the same reason: a Foundation with two bases or none
  // becomes impossible rather than merely unlikely.
  const BASE_OF_FOUNDATION = {};
  Object.keys(ENTRIES).forEach(id => { BASE_OF_FOUNDATION[ENTRIES[id].foundation] = id; });

  // ---------------- Lookup ----------------

  function get(passiveId) { return (passiveId && ENTRIES[passiveId]) || null; }
  function all() {
    return Object.keys(ENTRIES).map(k => ENTRIES[k])
      .sort((a, b) => TIMING[a.timing].order - TIMING[b.timing].order ||
                      a.name.localeCompare(b.name));
  }

  // THE STYLE -> FOUNDATION -> BASE RESOLUTION, for callers that hold a fighter or a style rather
  // than an equipped id. window.FAMILY_OF_STYLE (js/cards.js) is the style -> Foundation map and is
  // NOT duplicated here; it has shipped since the family pass and deck-rules already reads it.
  // Accepts a fighter object too, and prefers its `passiveId` when it has one — which is what makes
  // every caller here already correct on the day a deck can equip something other than its base.
  function forFighter(styleOrFighter) {
    if (styleOrFighter && typeof styleOrFighter === 'object') {
      if (styleOrFighter.passiveId) return get(styleOrFighter.passiveId);
      return forFighter(styleOrFighter.style);
    }
    const fam = (window.FAMILY_OF_STYLE || {})[styleOrFighter];
    return (fam && get(BASE_OF_FOUNDATION[fam])) || null;
  }

  // By the passive's own name, which is what a log line carries. Events carry `passiveId` too and
  // consumers should prefer that; this is the fallback for anything holding only prose.
  function byName(name) {
    const hit = Object.keys(ENTRIES).find(k => ENTRIES[k].name === name);
    return hit ? ENTRIES[hit] : null;
  }
  // The entry an event is about, preferring the id it carries.
  function forEvent(ev) {
    if (!ev) return null;
    return get(ev.passiveId) || byName(ev.passive) || forFighter(ev.style);
  }
  function timingOf(entry) { return entry ? TIMING[entry.timing] : null; }

  function styleLabel(style) {
    return (window.STYLE_LABELS && window.STYLE_LABELS[style]) || style;
  }
  function foundationLabel(entry) {
    return entry ? styleLabel(entry.foundation) : '';
  }

  // ---------------- Rendering ----------------
  //
  // THE HUD LINE IS THE ANCHOR, and the reason it is the HUD rather than Setup is that Setup is a
  // screen a player taps through once. Zone 1 is on screen for every exchange of every bout, it
  // already prints the style name on its own line, and both fighters have one -- so the existence
  // of passives, the name of yours, and the name of theirs all cost a single line that was already
  // being spent. The name is a button; the rule is one tap away rather than in a `title=` nobody on
  // a phone can reach.
  //
  // The signature still takes a style because js/ui-shell.js has a fighter's style at the point it
  // renders, and forFighter() does the resolution -- so the equip change does not reach that file.
  function hudHtml(styleOrFighter, opts) {
    const entry = forFighter(styleOrFighter);
    const style = (styleOrFighter && styleOrFighter.style) || styleOrFighter;
    const label = escapeHtml(styleLabel(style));
    if (!entry) return label;
    const canOpen = !!(window.UISheet && window.UISheet.open);
    const name = escapeHtml(entry.name);
    if (!canOpen) return `${label} <span class="fp-passive">${name}</span>`;
    return `${label} <button type="button" class="fp-passive is-tappable" ` +
           `data-passive-id="${escapeHtml(entry.id)}"` +
           `${opts && opts.onclick ? ` onclick="${escapeHtml(opts.onclick)}"` :
              ` onclick="StylePassives.openSheet(this)"`} ` +
           `title="${escapeHtml(entry.oneLiner)}" ` +
           `aria-label="${escapeHtml(entry.name + ', ' + foundationLabel(entry) + ' passive. ' + entry.oneLiner)}">` +
           `${name}</button>`;
  }

  // The beat callout: what just fired, and what it did. Composed from the EVENT rather than from
  // the table alone, because the event knows things the table cannot -- how much of a track shift
  // the cap actually allowed, which fighter a status landed on, whether the claim landed at all.
  function firedText(ev, names) {
    const entry = forEvent(ev);
    if (!entry) return '';
    const who = (k) => (names && names[k]) || (k === 'p1' ? 'Player 1' : 'Player 2');
    if (ev.kind === 'power') return `+${ev.amount || 1} Power to this Attack`;
    if (ev.kind === 'status') {
      // `applied: false` means the passive claimed something and a printed immunity refused it.
      // Said rather than swallowed, for the same reason a capped track shift is: a label that
      // fires and shows nothing reads as broken.
      if (ev.applied === false) return `${who(ev.target)} refuses it`;
      // Uprooted's second half: their base was already gone, so the throw buys the finish instead.
      // The two outcomes are one rule and read as one line — see the entry's `effect`.
      return ev.status === 'Opening'
        ? `${who(ev.target)} left open at ${ev.level}`
        : `${who(ev.target)} is Off-Balance`;
    }
    // An Intent taken off them for next exchange. Phrased from the loser's side and naming the
    // Intent, because "cannot Pressure" is the fact a player has to carry into their next commit —
    // the restriction surfaces again there (js/ui.js), and the two sentences should agree.
    if (ev.kind === 'restrict') {
      return `${who(ev.target)} cannot ${ev.intent} next exchange` +
             (ev.guardNext ? ` · +${ev.guardNext} Guard` : '');
    }
    if (ev.kind === 'intent') return `${ev.intent} wins the contest`;
    if (ev.capped) return `already at the limit of ${ev.direction}`;
    if (ev.direction) return `${ev.amount || 1} toward ${ev.direction}`;
    return entry.effect;
  }

  function firedHtml(events, names) {
    const list = (events || []).filter(e => e.type === 'style.passiveResolved');
    if (!list.length) return '';
    const rows = list.map(ev => {
      const entry = forEvent(ev);
      if (!entry) return '';
      const owner = names && names[ev.fighter] ? escapeHtml(names[ev.fighter]) + ' · ' : '';
      const canOpen = !!(window.UISheet && window.UISheet.open);
      const nameHtml = canOpen
        ? `<button type="button" class="sp-name is-tappable" data-passive-id="${escapeHtml(entry.id)}" ` +
          `onclick="StylePassives.openSheet(this)">${escapeHtml(entry.name)}</button>`
        : `<b class="sp-name">${escapeHtml(entry.name)}</b>`;
      return `<span class="sp-fired" title="${escapeHtml(entry.trigger)}">` +
             `<span class="sp-tag">PASSIVE</span>${owner}${nameHtml}` +
             `<span class="sp-effect">${escapeHtml(firedText(ev, names))}</span></span>`;
    }).join('');
    return rows ? `<div class="sp-fired-row" aria-label="Foundation passive">${rows}</div>` : '';
  }

  // The Foundation attribution goes in the BODY, not in a `subtitle` option -- js/ui-sheet.js has no
  // such option and would have dropped it without a word, which is the failure mode where the sheet
  // opens, looks right, and quietly does not say what it belongs to.
  function sheetBodyHtml(entry) {
    const t = timingOf(entry);
    return `<p class="sp-sheet-kicker">${escapeHtml(foundationLabel(entry))} · Foundation passive · ` +
           `${escapeHtml(t.label)}</p>` +
           `<p class="mech-sheet-rule">${escapeHtml(entry.oneLiner)}</p>` +
           `<div class="mech-sheet-row"><span class="mech-sheet-key">When</span>` +
           `<span class="mech-sheet-val">${escapeHtml(entry.trigger)}</span></div>` +
           `<div class="mech-sheet-row"><span class="mech-sheet-key">Then</span>` +
           `<span class="mech-sheet-val">${escapeHtml(entry.effect)}</span></div>` +
           `<div class="mech-sheet-row"><span class="mech-sheet-key">Timing</span>` +
           `<span class="mech-sheet-val">${escapeHtml(t.when)}</span></div>` +
           `<p class="mech-sheet-long">${escapeHtml(entry.long)}</p>` +
           `<p class="mech-sheet-sample"><b>For example:</b> ${escapeHtml(entry.sample)}</p>`;
  }

  function openSheet(elOrId) {
    let id = elOrId;
    if (elOrId && typeof elOrId === 'object' && elOrId.getAttribute) {
      id = elOrId.getAttribute('data-passive-id');
    }
    const entry = get(id) || byName(id) || forFighter(id);
    if (!entry) return false;
    if (window.MechanicsTelemetry && window.MechanicsTelemetry.note) {
      window.MechanicsTelemetry.note('passiveSheetOpened', entry.id);
    }
    if (!window.UISheet || !window.UISheet.open) return false;
    window.UISheet.open({
      title: entry.name,
      bodyHtml: sheetBodyHtml(entry),
      actions: [{ label: 'Got it', kind: 'primary' }]
    });
    return true;
  }

  window.StylePassives = {
    TIMING, ENTRIES, BASE_OF_FOUNDATION,
    get, all, byName, forFighter, forEvent, timingOf, styleLabel, foundationLabel,
    hudHtml, firedHtml, firedText, sheetBodyHtml, openSheet
  };
})();
